import { useState } from 'react';
import { useKV } from '@/hooks/useLocalStorage';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  BookmarkSimple,
  ChartLine,
  Database,
  DotsThree,
  Lightning,
  Plus,
  ShieldCheck,
  Star,
  Trash,
  Users,
} from '@phosphor-icons/react';
import { FilterPreset, FilterState } from '@/lib/types';
import { toast } from 'sonner';

interface FilterPresetsProps {
  currentFilters: FilterState;
  onApplyPreset: (preset: FilterPreset) => void;
  availableCategories: string[];
  availableDataTypes: string[];
}

// Default presets that come with the app
const defaultPresets: FilterPreset[] = [
  {
    id: 'high-quality-data',
    name: 'High Quality Data',
    description: 'Datasets with quality scores above 90%',
    icon: 'star',
    filters: {
      searchTerm: '',
      categories: [],
      dataTypes: [],
      qualityScore: 90,
      authRequired: null,
      sortBy: 'quality',
      sortOrder: 'desc',
    },
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'beginner-friendly',
    name: 'Beginner Friendly',
    description: 'No authentication required, well-documented APIs',
    icon: 'lightning',
    filters: {
      searchTerm: '',
      categories: ['Government', 'Education', 'Open Data'],
      dataTypes: [],
      qualityScore: 80,
      authRequired: false,
      sortBy: 'popularity',
      sortOrder: 'desc',
    },
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'financial-analysis',
    name: 'Financial Analysis',
    description: 'Financial and economic datasets with numeric data',
    icon: 'chart-line',
    filters: {
      searchTerm: 'financial economic stock market',
      categories: ['Finance', 'Economics'],
      dataTypes: ['number'],
      qualityScore: 85,
      authRequired: null,
      sortBy: 'quality',
      sortOrder: 'desc',
    },
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'social-research',
    name: 'Social Research',
    description: 'Demographics, social media, and survey data',
    icon: 'users',
    filters: {
      searchTerm: 'social demographic population survey',
      categories: ['Social', 'Demographics', 'Research'],
      dataTypes: ['string', 'number'],
      qualityScore: 75,
      authRequired: null,
      sortBy: 'datasets',
      sortOrder: 'desc',
    },
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
];

export function FilterPresets({
  currentFilters,
  onApplyPreset,
  availableCategories,
  availableDataTypes,
}: Readonly<FilterPresetsProps>) {
  const [customPresets, setCustomPresets] = useKV<FilterPreset[]>(
    'filter-presets',
    []
  );
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');
  const [newPresetIcon, setNewPresetIcon] = useState('bookmark-simple');

  const allPresets = [...defaultPresets, ...customPresets];

  const getPresetIcon = (iconName: string) => {
    switch (iconName) {
      case 'star':
        return <Star className='w-4 h-4' />;
      case 'lightning':
        return <Lightning className='w-4 h-4' />;
      case 'chart-line':
        return <ChartLine className='w-4 h-4' />;
      case 'users':
        return <Users className='w-4 h-4' />;
      case 'database':
        return <Database className='w-4 h-4' />;
      case 'shield-check':
        return <ShieldCheck className='w-4 h-4' />;
      default:
        return <BookmarkSimple className='w-4 h-4' />;
    }
  };

  const handleSavePreset = () => {
    if (!newPresetName.trim()) {
      toast.error('Please enter a preset name');

      return;
    }

    const newPreset: FilterPreset = {
      id: `custom-${Date.now()}`,
      name: newPresetName.trim(),
      description: newPresetDescription.trim() || 'Custom filter preset',
      icon: newPresetIcon,
      filters: currentFilters,
      createdAt: new Date().toISOString(),
    };

    setCustomPresets(prev => [...prev, newPreset]);
    setNewPresetName('');
    setNewPresetDescription('');
    setNewPresetIcon('bookmark-simple');
    setIsCreateDialogOpen(false);

    toast.success(`Saved preset "${newPreset.name}"`);
  };

  const handleDeletePreset = (presetId: string) => {
    setCustomPresets(prev => prev.filter(p => p.id !== presetId));
    toast.success('Preset deleted');
  };

  const hasActiveFilters =
    currentFilters.searchTerm ||
    currentFilters.categories.length > 0 ||
    currentFilters.dataTypes.length > 0 ||
    currentFilters.qualityScore > 0 ||
    currentFilters.authRequired !== null;

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <CardTitle className='flex items-center gap-2'>
            <BookmarkSimple className='w-5 h-5' />
            Filter Presets
          </CardTitle>
          <Dialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
          >
            <DialogTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='gap-2'
                disabled={!hasActiveFilters}
              >
                <Plus className='w-4 h-4' />
                Save Current
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Save Filter Preset</DialogTitle>
                <DialogDescription>
                  Save your current filter settings as a reusable preset
                </DialogDescription>
              </DialogHeader>

              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='preset-name'>Preset Name</Label>
                  <Input
                    id='preset-name'
                    value={newPresetName}
                    onChange={e => setNewPresetName(e.target.value)}
                    placeholder='e.g., Machine Learning Data'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='preset-description'>Description</Label>
                  <Textarea
                    id='preset-description'
                    value={newPresetDescription}
                    onChange={e => setNewPresetDescription(e.target.value)}
                    placeholder='Describe what this preset is useful for...'
                    rows={3}
                  />
                </div>

                <div className='space-y-2'>
                  <Label>Icon</Label>
                  <div className='flex gap-2'>
                    {[
                      {
                        name: 'bookmark-simple',
                        icon: <BookmarkSimple className='w-4 h-4' />,
                      },
                      { name: 'star', icon: <Star className='w-4 h-4' /> },
                      {
                        name: 'lightning',
                        icon: <Lightning className='w-4 h-4' />,
                      },
                      {
                        name: 'chart-line',
                        icon: <ChartLine className='w-4 h-4' />,
                      },
                      { name: 'users', icon: <Users className='w-4 h-4' /> },
                      {
                        name: 'database',
                        icon: <Database className='w-4 h-4' />,
                      },
                      {
                        name: 'shield-check',
                        icon: <ShieldCheck className='w-4 h-4' />,
                      },
                    ].map(({ name, icon }) => (
                      <Button
                        key={name}
                        variant={newPresetIcon === name ? 'default' : 'outline'}
                        size='sm'
                        onClick={() => setNewPresetIcon(name)}
                      >
                        {icon}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className='flex justify-end gap-2'>
                  <Button
                    variant='outline'
                    onClick={() => setIsCreateDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSavePreset}>Save Preset</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>

      <CardContent>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'>
          {allPresets.map(preset => (
            <div key={preset.id} className='relative group'>
              <Button
                variant='outline'
                className='w-full h-auto p-3 flex flex-col items-start gap-2 hover:bg-accent hover:border-accent-foreground/20 transition-all duration-200 hover:shadow-sm'
                onClick={() => onApplyPreset(preset)}
              >
                <div className='flex items-center gap-2 w-full'>
                  {getPresetIcon(preset.icon)}
                  <span className='font-medium text-sm truncate flex-1 text-left'>
                    {preset.name}
                  </span>
                  {preset.isDefault && (
                    <Badge variant='secondary' className='text-xs'>
                      Default
                    </Badge>
                  )}
                </div>
                <p className='text-xs text-muted-foreground text-left line-clamp-2'>
                  {preset.description}
                </p>
              </Button>

              {!preset.isDefault && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='ghost'
                      size='sm'
                      className='absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 h-6 w-6'
                    >
                      <DotsThree className='w-3 h-3' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem
                      onClick={() => handleDeletePreset(preset.id)}
                      className='text-destructive gap-2'
                    >
                      <Trash className='w-4 h-4' />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>

        {!hasActiveFilters && (
          <p className='text-center text-sm text-muted-foreground mt-4'>
            Apply some filters to save them as a preset
          </p>
        )}
      </CardContent>
    </Card>
  );
}
